import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, firstValueFrom } from 'rxjs';
import { Country } from '../models/country.model';
import { School } from '../models/school.model';
import { SchoolType } from '../models/school-type.enum';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';

export interface UserProfileDto {
	userId: string;
	email: string;
	teacherCode: string;
	country: string;
	name: string | null;
	director: string | null;
	type: SchoolType | null;
	address: string | null;
	city: string | null;
	postalCode: string | null;
	phone: string | null;
	contactEmail: string | null;
	principalName: string | null;
	uaiCode: string | null;
	accountantName: string | null;
	newsletter: boolean;
	lang: string | null;
	admin: boolean;
	credit: number;
}

@Injectable()
export class UserService {
	user = signal<{ id: string; email: string } | null>(null);
	account = signal<School | null>(null);
	accountType = signal<'scolaire' | 'particulier' | null>(null);

	constructor(
		private http: HttpClient,
		private router: Router,
		private api: ApiService,
	) {}

	getCountryList(): Observable<Country[]> {
		return this.http.get<Country[]>(`assets/data/countries.json`);
	}

	getClassesList(): Observable<{ [key: string]: { college: string[]; high_school: string[] } }> {
		return this.http.get<{ [key: string]: { college: string[]; high_school: string[] } }>(
			`assets/data/classes.json`,
		);
	}

	async completeSchoolSetup(data: {
		country: string;
		schoolName: string;
		schoolType: string;
		teacherCode: string;
	}): Promise<void> {
		await firstValueFrom(
			this.api.post('/users/school', {
				country: data.country,
				teacherCode: data.teacherCode,
				name: data.schoolName,
				type: data.schoolType,
			}),
		);
		await firstValueFrom(
			this.api.post('/school-classes', {
				label: 'Classe démo',
				grade: 'Demo',
				number: 0,
				order: 0,
			}),
		);
		await this.loadEpstopAccount();
		await this.navigateHome();
	}

	getHomeUrl(): string {
		if (this.accountType() === 'particulier') {
			return '/welcome/gymtop';
		}
		if (this.accountType() === 'scolaire' && !this.account()) {
			return '/welcome/sign-up/school';
		}
		if (this.account()) {
			return '/dashboard';
		}
		return '/welcome/sign-in';
	}

	async navigateHome(): Promise<void> {
		await this.router.navigateByUrl(this.getHomeUrl());
	}

	async setPreferences(data: Partial<School>): Promise<void> {
		const dto = {
			teacherCode: data.teacherCode,
			name: data.name,
			director: data.director,
			type: data.type,
			address: data.address,
			city: data.city,
			postalCode: data.postalCode,
			phone: data.phone,
			contactEmail: data.contactEmail,
			principalName: data.principalName,
			uaiCode: data.uaiCode,
			accountantName: data.accountantName,
			newsletter: data.newsletter,
			country: data.country,
			lang: data.lang,
		};
		const cleaned = Object.fromEntries(
			Object.entries(dto).filter(([, v]) => v !== undefined),
		);
		await firstValueFrom(this.api.patch('/users/school', cleaned));
		const acc = this.account();
		if (acc) {
			this.account.set({ ...acc, ...data });
		}
	}

	async updateCountry(country: string): Promise<void> {
		await this.setPreferences({ country });
	}

	async loadEpstopAccount(): Promise<boolean> {
		const me = await firstValueFrom(
			this.api.get<{ accountType: 'scolaire' | 'particulier' }>('/auth/me'),
		);
		this.accountType.set(me.accountType);

		const profile = await firstValueFrom(
			this.api.get<UserProfileDto | null>('/users/profile'),
		);

		if (!profile) {
			this.user.set({
				id: this.decodeUserIdFromToken() ?? '',
				email: this.decodeEmailFromToken() ?? '',
			});
			this.account.set(null);

			if (me.accountType === 'scolaire') {
				await this.router.navigateByUrl('/welcome/sign-up/school');
				return false;
			}
			return true;
		}

		this.user.set({ id: profile.userId, email: profile.email });
		this.account.set(this.mapProfileToSchool(profile));
		return true;
	}

	async updateEmail(newEmail: string): Promise<void> {
		await firstValueFrom(this.api.patch('/auth/change-email', { newEmail }));
		const acc = this.account();
		if (acc) {
			this.account.set({ ...acc, email: newEmail });
		}
		const u = this.user();
		if (u) {
			this.user.set({ id: u.id, email: newEmail });
		}
	}

	private mapProfileToSchool(p: UserProfileDto): School {
		return {
			email: p.email,
			userId: p.userId,
			teacherCode: p.teacherCode,
			country: p.country,
			creationDate: new Date(),
			newsletter: p.newsletter,
			address: p.address ?? undefined,
			city: p.city ?? undefined,
			director: p.director ?? undefined,
			name: p.name ?? undefined,
			type: (p.type ?? undefined) as SchoolType | undefined,
			postalCode: p.postalCode ?? undefined,
			phone: p.phone ?? undefined,
			contactEmail: p.contactEmail ?? undefined,
			principalName: p.principalName ?? undefined,
			uaiCode: p.uaiCode ?? undefined,
			accountantName: p.accountantName ?? undefined,
			lang: p.lang ?? undefined,
			admin: p.admin,
		};
	}

	private decodeUserIdFromToken(): string | null {
		const t = this.api.getAccessToken();
		if (!t) {
			return null;
		}
		try {
			const part = t.split('.')[1];
			const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
			return payload.sub ?? null;
		} catch {
			return null;
		}
	}

	private decodeEmailFromToken(): string | null {
		const t = this.api.getAccessToken();
		if (!t) {
			return null;
		}
		try {
			const part = t.split('.')[1];
			const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
			return payload.email ?? null;
		} catch {
			return null;
		}
	}
}
